/*
 * @Description: 发票开票记录列表 - 通用搜索字段
 * @Date: 2026-06-11
 */
import {
  useInputColumn,
  useSelectColumn,
  useDatePickerColumn
} from 'xrk-components';
import {
  INVOICE_TYPE,
  BLUE_INVOICE_STATUS,
  RED_INVOICE_STATUS
} from '@V/invoice/src/constants';

/** 状态映射对象类型（后端原始值作 key，平台传入子集时需此宽松类型） */
type StatusMap = Record<number, { label: string; value: number }>;

/** 下拉选项数组类型 */
type SelectOption = { label: string; value: number };

/** 发票申请编号 */
export const invoiceOrderNoSearch = () =>
  useInputColumn({
    prop: 'invoiceOrderNo',
    label: '发票申请编号',
    config: {
      clearable: true,
      maxlength: 50
    }
  });

/** 购买方 */
export const enterpriseNameSearch = () =>
  useInputColumn({
    prop: 'enterpriseName',
    label: '购买方',
    config: {
      clearable: true,
      maxlength: 50
    }
  });

/** 销售方 */
export const supplierNameSearch = () =>
  useInputColumn({
    prop: 'supplierName',
    label: '销售方',
    config: {
      clearable: true,
      maxlength: 50
    }
  });

/** 申请时间（日期范围，写入 createAtStart / createAtEnd） */
export const timeRangeSearch = (label = '申请时间') =>
  useDatePickerColumn({
    prop: 'time1',
    label,
    style: { width: '220px' },
    config: {
      type: 'daterange',
      startPlaceholder: '时间始',
      endPlaceholder: '时间止',
      clearable: true
    },
    listener: {
      onChange: (value, searchObj) => {
        if (!value) {
          searchObj.createAtStart = '';
          searchObj.createAtEnd = '';
        } else {
          searchObj.createAtStart = value[0];
          searchObj.createAtEnd = value[1];
        }
      }
    }
  });

/** 发票类型 */
export const invoiceTypeSearch = () =>
  useSelectColumn({
    prop: 'invoiceType',
    label: '发票类型',
    config: {
      placeholder: '请选择',
      clearable: true,
      options: INVOICE_TYPE
    }
  });

/** 蓝票状态（平台发票传入 PLATFORM_BLUE_INVOICE_STATUS 覆盖） */
export const blueInvoiceStatusSearch = (
  options: StatusMap = BLUE_INVOICE_STATUS
) =>
  useSelectColumn({
    prop: 'blueInvoiceStatus',
    label: '蓝票状态',
    config: {
      placeholder: '请选择',
      clearable: true,
      options
    }
  });

/** 红票状态（平台发票传入 PLATFORM_RED_INVOICE_STATUS 覆盖） */
export const redInvoiceStatusSearch = (
  options: StatusMap = RED_INVOICE_STATUS
) =>
  useSelectColumn({
    prop: 'redInvoiceStatus',
    label: '红票状态',
    config: {
      placeholder: '请选择',
      clearable: true,
      options
    }
  });

/** 项目名称（待开票列表 / 个体待开票通用） */
export const projectNameSearch = () =>
  useInputColumn({
    prop: 'projectName',
    label: '项目名称',
    config: {
      placeholder: '请输入',
      clearable: true
    }
  });

/** 订单编号（待开票列表通用） */
export const userDefinedOrderNoSearch = () =>
  useInputColumn({
    prop: 'userDefinedOrderNo',
    label: '订单编号',
    config: {
      placeholder: '请输入',
      clearable: true
    }
  });

/**
 * 订单类型（待开票列表通用）
 * @param options 订单类型选项（不同角色传入不同子集，来源 @constants/order ORDER_TYPE）
 * @param defaultValue 默认选中值；传入时下拉不可清空
 */
export const userDefinedOrderTypeSearch = (
  options: SelectOption[],
  defaultValue?: number
) =>
  useSelectColumn({
    prop: 'userDefinedOrderType',
    label: '订单类型',
    defaultValue,
    config: {
      clearable: defaultValue === undefined,
      placeholder: '请选择',
      options
    }
  });

/** 更新时间（日期范围，写入 authAtStart / authAtEnd） */
export const updateTimeSearch = () =>
  useDatePickerColumn({
    prop: 'time2',
    label: '更新时间',
    style: { width: '220px' },
    config: {
      type: 'daterange',
      startPlaceholder: '时间始',
      endPlaceholder: '时间止',
      clearable: true
    },
    listener: {
      onChange: (value, searchObj) => {
        if (!value) {
          searchObj.authAtStart = '';
          searchObj.authAtEnd = '';
        } else {
          searchObj.authAtStart = value[0];
          searchObj.authAtEnd = value[1];
        }
      }
    }
  });

/** 关联结算单号（平台待开票） */
export const settlementNoSearch = () =>
  useInputColumn({
    prop: 'settlementNo',
    label: '关联结算单号',
    config: { placeholder: '请输入关联结算单号', clearable: true }
  });

/** 关联项目服务费订单号（平台待开票） */
export const projectServiceFeeOrderNoSearch = () =>
  useInputColumn({
    prop: 'projectServiceFeeOrderNo',
    label: '关联项目服务费订单号',
    config: { placeholder: '请输入', clearable: true }
  });

/** 结算月度 */
export const taskAtSearch = () =>
  useDatePickerColumn({
    prop: 'taskAt',
    label: '结算月度',
    config: {
      type: 'month',
      clearable: true
    }
  });
